import { GetLeadResponse } from "@/modules/leads/dtos";
import { getJourney } from "@/modules/lead-page/api/lead-page.v2.api";
import { JourneyRelationType } from "../types";

export async function getLeadMapping({
  id,
  signal,
  mode,
  hasQRCode,
}: {
  id?: string;
  signal?: AbortSignal;
  mode?: JourneyRelationType;
  hasQRCode?: boolean;
}): Promise<GetLeadResponse> {
  if (!id) {
    throw new Error("No lead ID provided");
  }

  const { data: journey } = await getJourney({
    id,
    signal,
    mode,
    hasQRCode,
  });

  const { lead, status, user, rubbish, nanoId, uid, propertyId } = journey;
  const { customer, leadType, campaign, methodOfContact, leadTags } = lead;

  const mappedLead: GetLeadResponse = {
    data: {
      rubbish: rubbish,
      mergedChild: !!journey?.mergeRecordAsOld,
      mergedParent: !!journey.mergeRecordsAsNew?.length,
      lead_type: {
        id: leadType?.uid,
        form: leadType?.form,
        lead_type: leadType?.name,
        managerReassignmentValidity: leadType?.managerReassignmentValidity,
        default: leadType?.default,
      },
      leadIcons: lead?.launch?.leadIcon ? [lead.launch.leadIcon] : [],
      nanoId,
      propertyId,
      uid,
      lead: {
        leadTags: leadTags,
        organization: lead?.organization,
        journeys: lead?.journeys,
        rubbish: lead?.rubbish,
        duplicate: lead?.duplicate,
        parked: lead?.parked,
        database: lead?.database,
        launch: lead?.launch ,
        partner: lead?.partner,
        referral: lead?.referral,
        merged: lead?.duplicate,
        archived: lead?.archived,
        email:
          customer?.emails?.find((email) => email.default === true)?.email ??
          "",
        best_time_to_call: customer?.bestTimeToCall ?? "",
        customer_uid: customer?.uid,
        journey_uid: journey?.uid,
        ad_id: lead?.ad?.id,
        oldId: lead?.oldId,
        ad: lead?.ad ? { name: lead.ad.name, uid: lead.ad.uid } : undefined,
        adset_id: lead?.adset?.id,
        adset: lead?.adset
          ? { name: lead.adset.name, uid: lead.adset.uid }
          : undefined,
        name: customer?.name,
        lead_telephones:
          customer?.telephones?.map((telephone, index) => ({
            uid: telephone?.uid,
            telephone: telephone?.telephone,
            lead_id: lead?.id,
            id: index,
            deletedAt: telephone.deletedAt ?? null,
            primary: telephone?.default,
          })) ?? [],
        id: lead?.id,
        uid: lead?.uid,
        date_of_last_contact: journey?.dateOfLastContact ?? "",
        campaign: campaign
          ? { name: campaign?.name, uid: campaign?.uid }
          : undefined,
        status: {
          id: status?.uid,
          status: status?.name,
          color: status?.color,
          status_category_id: status?.statusCategoryId,
          status_forms_id: status?.formId ?? 0,
          erealtyForm: status?.erealtyForm,
          new_color: status?.color,
          old_color: status?.oldColor,
          statusCategory: {
            uid: status?.statusCategory?.uid,
            deletedAt: status?.statusCategory?.deletedAt,
            createdAt: status?.statusCategory?.createdAt,
            updatedAt: status?.statusCategory?.updatedAt,
            name: status?.statusCategory?.name,
          },
          nextStatusCategory: {
            uid: status?.nextStatusCategory?.uid ?? "",
            deletedAt: status?.nextStatusCategory?.deletedAt ?? null,
            createdAt: status?.nextStatusCategory?.createdAt ?? "",
            updatedAt: status?.nextStatusCategory?.updatedAt ?? null,
            name: status?.nextStatusCategory?.name ?? "",
          },
        },
        location: {
          uid: lead?.location?.uid ?? "",
          deletedAt: lead?.location?.deletedAt ?? null,
          createdAt: lead?.location?.createdAt ?? "",
          updatedAt: lead?.location?.updatedAt ?? "",
          name: lead?.location?.name ?? "",
          locationType: lead?.location?.locationType,
          compoundId: lead?.location?.compoundId,
          isExpo: lead?.location?.isExpo,
        },
        lead_type_id: leadType?.id,
        lead_type: {
          lead_type: leadType?.name,
          uid: leadType?.uid,
        },
        leadType,
        method_of_contact_id: methodOfContact?.uid,
        method_of_contact: {
          name: methodOfContact?.name,
          id: methodOfContact?.uid,
        },
        methodOfContact: methodOfContact,
        date_of_last_request: lead?.dateOfLastRequest ?? "",
        telephone: "",
        desc: "",
        created_at: "",
        updated_at: lead?.updatedAt,
        user_id: lead?.id,
        status_id: 0,
        reassigned: false,
        had_meeting: false,
        do_not_call: customer?.doNotCall,
        last_updater_id: 0,
        customer_id: 0,
        lead_infos: lead?.leadInfos,
        lead_source_id: 0,
        user: {
          name: user?.name ?? "",
          color: user?.color ?? "",
          new_color: user?.color ?? "",
          id: user?.uid ?? "",
        },
        lead_source: { name: "" },
        customer: { lead_telephones: [], leads: customer?.leads },
        validity_time: lead?.validityTime,
      },
      user_name: user?.name ?? "",
      user_color: user?.color ?? "",
      new_user_color: user?.color ?? "",
      lead_history: [],
      isOnCall: journey?.isOnCall,
    },
  };

  return mappedLead;
}
